import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { DateTimeProvider, OAuthModule, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { RECAPTCHA_LOADER_OPTIONS } from 'ng-recaptcha-2';
import { filter, lastValueFrom, take } from 'rxjs';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { EXCLUDE_OAUTH_INTERCEPTOR_HEADER } from './core/interceptors/oauth-interceptor.constants';
import { AppConfigService } from './core/services/app-config.service';
import { AuthService } from './core/services/auth';
import { AuthProxyService } from './core/services/auth/auth-proxy.service';
import { GatewayAuthService } from './core/services/auth/gateway-auth.service';
import { KeycloakAuthService } from './core/services/auth/keycloak-auth.service';
import { CustomDateTimeProvider } from './core/services/custom-date-provider.service';
import { CustomIconsService } from './core/services/custom-icons.service';
import { PasswordExpirationService } from './core/services/password-expiration.service';
import { PhoneNumberCheckerService } from './core/services/phone-number-checker.service';
import { ApiCallStateActions } from './core/state/api-call-state/api-call-state.actions';
import { createReceivedInvoicesApiCallState } from './core/state/api-call-state/initial-api-call-states/received-invoices.api-call-state';
import { createSentInvoicesApiCallState } from './core/state/api-call-state/initial-api-call-states/sent-invoices.api-call-state';
import { StateModule } from './core/state/state.module';
import { SharedModule } from './shared/shared.module';

registerLocaleData(localeIt);

function authAppInitializer(appConfig: AppConfigService, oauthService: OAuthService, httpClient: HttpClient) {
  return async () => {
    try {
      await appConfig.loadConfig();
    } catch (error) {
      console.error('Failed to detect country:', error);
      throw new Error('Could not determine country for Keycloak configuration');
    }
    const config = appConfig.getConfig();

    const keycloakConfig = appConfig.getKeycloakConfig();

    let timeDifferenceInSeconds = 0;

    if (config!.dateApiUrl) {
      const date$ = httpClient.get(config!.dateApiUrl, {
        headers: {
          [EXCLUDE_OAUTH_INTERCEPTOR_HEADER]: 'skip',
        },
      });
      const serverDate: any = await lastValueFrom(date$);
      const date = new Date();
      const serverDateInSeconds = Math.floor(serverDate.data);
      const clientDateInSeconds = Math.floor(date.getTime() / 1000);
      timeDifferenceInSeconds = serverDateInSeconds - clientDateInSeconds;
    }

    localStorage.setItem('time_difference_in_seconds', timeDifferenceInSeconds.toString());

    // Configure OAuthService only if gatewayLogin is false
    if (!config!.gatewayLogin) {
      oauthService.configure({
        issuer: keycloakConfig!.issuerUrl,
        clientId: keycloakConfig!.clientId,
        scope: keycloakConfig!.clientScope,
        redirectUri: window.location.origin + '/auth-redirect',
        postLogoutRedirectUri: window.location.origin,
        responseType: 'code',
        showDebugInformation: true,
        clockSkewInSec: 60,
        timeoutFactor: 0.75,
        sessionChecksEnabled: false,
        clearHashAfterLogin: false,
        logoutUrl: keycloakConfig?.logoutUrl ?? ``,
      });
    }
  };
}

function appInitialization(
  customIconService: CustomIconsService,
  passwordExpirationService: PasswordExpirationService,
  phoneNumberCheckerService: PhoneNumberCheckerService
) {
  return () => {
    customIconService.init();
    passwordExpirationService.init();
    phoneNumberCheckerService.init();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    CoreModule,
    OAuthModule.forRoot(),
    StateModule,
    BrowserAnimationsModule,
  ],
  providers: [
    { provide: OAuthStorage, useFactory: oAuthStorageFactory },
    provideAppInitializer(() => {
      const initializerFn = authAppInitializer(
        inject(AppConfigService),
        inject(OAuthService),
        inject(HttpClient)
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appInitialization(
        inject(CustomIconsService),
        inject(PasswordExpirationService),
        inject(PhoneNumberCheckerService)
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const appConfig = inject(AppConfigService);
      const store = inject(Store);
      return lastValueFrom(
        appConfig.configLoaded$.pipe(
          filter(loaded => loaded),
          take(1)
        )
      ).then(() => {
        const countryConfig = appConfig.getCountryConfig();
        store.dispatch(
          ApiCallStateActions.initializeCountryStates({
            apiCallStates: [
              createReceivedInvoicesApiCallState(
                countryConfig.receivedInvoice.statusMap,
                countryConfig.receivedInvoice.statuses,
                countryConfig.receivedInvoice.paymentStatusesMap
              ),
              createSentInvoicesApiCallState(
                countryConfig.sentInvoice.statusMap,
                countryConfig.sentInvoice.paymentStatusesMap
              ),
            ],
          })
        );
      });
    }),
    KeycloakAuthService,
    GatewayAuthService,
    {
      provide: AuthService,
      useClass: AuthProxyService,
    },
    { provide: DateTimeProvider, useClass: CustomDateTimeProvider },
    { provide: LOCALE_ID, useValue: 'it-IT' },
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: (translocoService: TranslocoService) => ({
        onBeforeLoad(url: URL) {
          url.searchParams.set('hl', translocoService.getActiveLang());
          return { url };
        },
      }),
      deps: [TranslocoService],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}

export function oAuthStorageFactory(): OAuthStorage {
  return localStorage;
}
